import useWindowSize from './useWindowSize';
import {
  Bar,
  BarChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import {
  BAR_MAX_SIZE,
  CHART_DATASETS,
  LABEL_LIST_MIN_Y_GAP,
  LABEL_LIST_TEXT_FONT_SIZE,
  RENAME_MAP_NEW
} from '../../shared/AppConstants';
import { create2DArray, updateSuggestedLabelListXCoords } from './helper';
import { CustomXTickText } from './CustomXTickText';
import { CustomLabelYText } from './CustomLabelYText';
import { CustomYTickText } from './CustomYTickText';
import { CustomLabelList } from './CustomLabelList';
import { CustomLegend } from './CustomLegend';
import { formatAmountWithCommas } from '../../shared/UtilFunctions';
import { CustomToolTip } from './CustomTooltip';
import { useTheme } from '@mui/material/styles';

export default function SpendChart({
  activeBarIndex = -1,
  disabledDatasetIndices = new Set(),
  height = 300,
  spendData,
  onToggleDataset = () => {},
  chart_colors,
  chart_datasets,
  rename_map,
  singleLineLegend = false
}) {
  const { width } = useWindowSize();
  const theme = useTheme();

  if (!spendData.length) {
    return (
      <div
        style={{
          height,
          width: '100%'
        }}
      />
    );
  }

  const data = [...spendData];
  const numDatasets = Object.keys(chart_datasets).length;
  const numBars = spendData.length;
  const legendRows = singleLineLegend || numDatasets <= 2 ? 1 : 2;
  const legendHeight = singleLineLegend
    ? 70
    : Math.max(80, legendRows * 36 + 12);

  let suggestedLabelListYCoords = create2DArray(numDatasets, numBars);

  const renderCustomizedLabelList = (props) => {
    const isDisplay = false;

    props = {
      ...props,
      isDisplay
    };

    suggestedLabelListYCoords = updateSuggestedLabelListXCoords(
      suggestedLabelListYCoords,
      props
    );

    return (
      <CustomLabelList
        activeBarIndex={activeBarIndex}
        key={`custom-label-list-${props.index}`}
        suggestedY={
          suggestedLabelListYCoords[props.datasetIndex][props.index].position
        }
        {...props}
      />
    );
  };

  const renderCustomizedLegend = (props) => (
    <CustomLegend
      disabledDatasetIndices={disabledDatasetIndices}
      onToggleDataset={onToggleDataset}
      chartColors={chart_colors}
      chartDatasets={chart_datasets}
      renameMap={rename_map}
      singleLineLegend={singleLineLegend}
      {...props}
    />
  );

  function customizedPayload(payload) {
    if (!payload || payload?.length === 0) return [];
    let spendValues = calculateUtilizationAndSpend(payload[0].payload);
    const NUMERATOR_INDEX = 0;
    const PERCENTAGE_INDEX = 1;
    const DENOMINATOR_INDEX = 2;

    return payload
      .map((p, index) => {
        const newDataKey =
          index === NUMERATOR_INDEX
            ? 'numerator'
            : index === PERCENTAGE_INDEX
              ? 'percentage'
              : index === DENOMINATOR_INDEX
                ? 'denominator'
                : p.dataKey;

        const formattedPayload = {
          ...p.payload,
          numerator:
            index === 0
              ? `${formatAmountWithCommas(spendValues.numerator, true)}`
              : undefined,
          percentage: index === 1 ? `${spendValues.percentage}%` : undefined,
          denominator:
            index === 2
              ? `${formatAmountWithCommas(spendValues.denominator, true)}`
              : undefined
        };

        return {
          ...p,
          dataKey: newDataKey,
          payload: formattedPayload
        };
      })
      .filter(
        (p) =>
          p.dataKey === 'numerator' ||
          p.dataKey === 'percentage' ||
          p.dataKey === 'denominator'
      );
  }

  const formatAllSpendPayload = (payload) => {
    if (payload.length === 0) return [];

    return payload.map((p) => {
      const valueObj = p.payload.values;

      const newValues = Object.fromEntries(
        Object.entries(valueObj).map(([key, value]) => [
          key,
          formatAmountWithCommas(value, true)
        ])
      );

      return {
        ...p,
        payload: {
          ...p.payload,
          values: newValues
        }
      };
    });
  };

  const renderCustomizedTooltip = (props) => {
    const { active, payload, label } = props;

    let isStrategySpend =
      Object.values(chart_datasets).filter((value) => value === 'grow').length >
      0;
    let tempPayload = isStrategySpend
      ? customizedPayload(payload)
      : formatAllSpendPayload(payload);
    let customizedRenameMap = isStrategySpend ? RENAME_MAP_NEW : rename_map;

    if (active) {
      return (
        <CustomToolTip
          active={active}
          payload={tempPayload}
          renameMap={customizedRenameMap}
          title={
            label === 'Forecast'
              ? `52 Week Spend ${label}`
              : label === 'Invoiced'
                ? 'Current R12 Invoice Spend'
                : label
          }
        />
      );
    }
    return null;
  };

  function calculateUtilizationAndSpend(payload) {
    let numerator = 0;
    let denominator = 0;

    Object.values(chart_datasets).forEach((key, index) => {
      if (!disabledDatasetIndices.has(index)) {
        let value = payload.values[key];

        if (
          key !== CHART_DATASETS.REDUCE_TOTAL &&
          key !== CHART_DATASETS.DONOTUSE_TOTAL &&
          key !== CHART_DATASETS.LIMITED_USE_TOTAL
        ) {
          numerator += value;
        }
        denominator += value;
      }
    });

    return {
      numerator: numerator,
      denominator: denominator,
      percentage:
        denominator > 0 ? Math.round((numerator / denominator) * 100) : 0
    };
  }

  const renderLabelY = () => <CustomLabelYText dx={-100} dy={40} />;
  const CustomBarWithTarget = (props) => {
    const { fill, x, y, width, height } = props;
    if (
      props.payload.percentage === null ||
      props.payload.percentage === '' ||
      isNaN(props.payload.percentage)
    )
      return <></>;

    return (
      <svg>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          stroke="none"
          fill={fill}
        />
        <line
          x1={x}
          x2={x + (width + 25)}
          y1={y}
          y2={y}
          stroke={theme.fuel6Tokens.JddcColorTextPrimary}
          strokeWidth={1}
          strokeDasharray={'6 3'}
        />
        <text
          fill={theme.fuel6Tokens.JddcColorTextPrimary}
          x={x + width + 26}
          y={y + 5}
        >
          {calculateUtilizationAndSpend(props.payload).percentage}%
        </text>
      </svg>
    );
  };

  return (
    <>
      <ResponsiveContainer height={height} width={'99%'}>
        <BarChart
          data={data}
          margin={{
            top: LABEL_LIST_MIN_Y_GAP * (numDatasets - 1)
          }}
          stackOffset="sign"
        >
          <XAxis
            dataKey="spendType"
            interval={0}
            tick={<CustomXTickText />}
            type="category"
            height={50}
            tickMargin={10}
          />
          <YAxis
            label={renderLabelY}
            tick={<CustomYTickText />}
            type="number"
          />
          <Tooltip
            content={renderCustomizedTooltip}
            cursor={false}
            isAnimationActive={false}
          />
          {Object.values(chart_datasets).map((value, datasetIndex) => {
            return disabledDatasetIndices.has(datasetIndex) ? null : (
              <Bar
                dataKey={`values.${value}`}
                fill={chart_colors[value]}
                isAnimationActive={false}
                key={value}
                legendType="circle"
                maxBarSize={BAR_MAX_SIZE}
                stackId="a"
                shape={datasetIndex === 2 ? <CustomBarWithTarget /> : {}}
              >
                {width >= 900 && (
                  <LabelList
                    content={(props) =>
                      renderCustomizedLabelList({
                        ...props,
                        dataset: value,
                        datasetIndex
                      })
                    }
                    dataKey={`values.${value}`}
                    position="center"
                  />
                )}
              </Bar>
            );
          })}

          <Legend
            align="center"
            content={renderCustomizedLegend}
            verticalAlign="bottom"
            height={legendHeight}
          />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}
